import axios from 'axios';
 
// ============= ENCRYPTION UTILITIES =============
 
const SECRET_KEY_STRING =  process.env.REACT_APP_VITE_SECRET_KEY;
console.log("FRONT KEY:", SECRET_KEY_STRING);
const IV_LENGTH = 12;
 
async function getKey() {
    const encoder = new TextEncoder();
    const keyData = encoder.encode(SECRET_KEY_STRING);
 
    const hashBuffer = await crypto.subtle.digest("SHA-256", keyData);
 
    return crypto.subtle.importKey(
        "raw",
        hashBuffer,
        { name: "AES-GCM", length: 256 },
        false,
        ["encrypt", "decrypt"]
    );
}
 
/**
 * Encrypting data using AES-256-GCM
 */
async function encrypt(data) {
    const text = typeof data === 'string' ? data : JSON.stringify(data);
    const encoder = new TextEncoder();
    const key = await getKey();
 
    const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
 
    const encrypted = await crypto.subtle.encrypt(
        {
            name: "AES-GCM",
            iv,
            tagLength: 128
        },
        key,
        encoder.encode(text)
    );
 
    const encryptedArray = new Uint8Array(encrypted);
    const combined = new Uint8Array(IV_LENGTH + encryptedArray.length);
    combined.set(iv, 0);
    combined.set(encryptedArray, IV_LENGTH);
 
    return btoa(String.fromCharCode(...combined));
}
 
/**
 * Decrypting data using AES-256-GCM
 */
async function decrypt(encryptedData) {
    const key = await getKey();
 
    const combined = Uint8Array.from(atob(encryptedData), c => c.charCodeAt(0));
 
    const iv = combined.slice(0, IV_LENGTH);
    const ciphertext = combined.slice(IV_LENGTH);
 
    const decrypted = await crypto.subtle.decrypt(
        {
            name: "AES-GCM",
            iv,
            tagLength: 128
        },
        key,
        ciphertext
    );
 
    const decoder = new TextDecoder();
    const text = decoder.decode(decrypted);
 
    try {
        return JSON.parse(text);
    } catch {
        return text;
    }
}
 
// ============= INTERNET CHECK UTILITIES =============


function checkonline() {
  return navigator.onLine;
}

 
// ============= AXIOS CONFIGURATION =============
 
// const apiaxios = axios.create({
//     timeout: 1000000,
//     withCredentials: true,
// });


const apiaxios = axios.create({
    baseURL: process.env.REACT_APP_API_URL, 
    timeout: 1000000,
    withCredentials: true,
});

 
// ============= REQUEST INTERCEPTOR (INTERNET CHECK + ENCRYPTION) =============
apiaxios.interceptors.request.use(
    async (config) => {
 
      // debugger
        const isOnline = checkonline();
 
        if (!isOnline) {
            return Promise.reject({
                response: {
                    status: '503',
                    message: 'No internet connection. Please connect to the internet.'
                },
                isNetworkError: true
            });
        }
 
        // Encrypting request body if present
        if (config.data && (config.method === 'post' || config.method === 'put' || config.method === 'patch')) {
            try {
                const encryptedData = await encrypt(config.data);
                config.data = { data: encryptedData };
            } catch (error) {
                console.error('Encryption error:', error);
                return Promise.reject({
                    response: {
                        status: '500',
                        message: 'Failed to encrypt request data'
                    }
                });
            }
        }
 
        return config;
    },
    (error) => {
        return Promise.reject({
            response: {
                status: 'request_error',
                message: 'Failed to prepare request'
            }
        });
    }
);
 
// ============= RESPONSE INTERCEPTOR (DECRYPTION + ERROR HANDLING) =============
apiaxios.interceptors.response.use(
    async (response) => {
        // Decrypt response data if present
        if (response.data) {
            try {
                const decryptedData = await decrypt(response.data);
                response.data = decryptedData;
                console.log('Decrypted response data:', decryptedData);
            } catch (error) {
                console.error('Decryption error:', error);
            }
        }
 
        return response;
    },
    (error) => {
        // If it's already our custom network error from request interceptor
        if (error.isNetworkError) {
            return Promise.reject(error);
        }
 
        // Handling server response errors
        if (error.response) {
            const statusCode = error.response.status || error.response.data?.status || 'unknown';
 
            switch (statusCode) {
                case 429:
                    return Promise.reject({
                        response: {
                            status: "401",
                            message: "something went wrong. Please try again later"
                        }
                    });
 
                case 401:
                    return Promise.reject({
                        response: {
                            status: "401",
                            message: "Unauthorized access"
                        }
                    });
 
                case 419:
                    return Promise.reject({
                        response: {
                            status: "419",
                            message: "session expired"
                        }
                    });
 
                default:
                    return Promise.reject({
                        response: {
                            status: "500",
                            message: "something went wrong. Please try again later"
                        }
                    });
            }
        }
 
        // Handling network/connection errors
        if (error.code) {
            switch (error.code) {
                case 'ERR_NETWORK':
                case 'NETWORK_ERROR':
                    return Promise.reject({
                        response: {
                            status: '503',
                            message: 'Network connection failed. Please check your internet connection.'
                        }
                    });
 
                case 'ECONNABORTED':
                case 'ERR_CANCELED':
                    return Promise.reject({
                        response: {
                            status: '408',
                            message: 'Request timeout. Please try again.'
                        }
                    });
 
                case 'ERR_CONNECTION_REFUSED':
                    return Promise.reject({
                        response: {
                            status: '503',
                            message: 'Connection refused. Server may be down.'
                        }
                    });
 
                case 'ERR_BAD_REQUEST':
                    return Promise.reject({
                        response: {
                            status: '500',
                            message: 'Bad request. Please check your input.'
                        }
                    });
 
                default:
                    return Promise.reject({
                        response: {
                            status: '503',
                            message: 'Network error occurred. Please try again.'
                        }
                    });
            }
        }
 
        return Promise.reject({
            response: {
                status: "500",
                message: "something went wrong. Please try again later"
            }
        });
    }
);
 
// ============= NETWORK STATUS MONITORING =============
 
let isOnlineStatus = navigator.onLine;
 
window.addEventListener('online', () => {
    isOnlineStatus = true;
});
 
window.addEventListener('offline', () => {
    isOnlineStatus = false;
});
 
export const getCurrentNetworkStatus = () => navigator.onLine;
export { checkonline };
export default apiaxios;
 